import { Navbar, Sidebar, LandingNavbar } from "@/ui/components/navigation";
import {
  SidebarProvider,
  useSidebar,
} from "@/application/contexts/SidebarContext";
import { PageLayoutProps } from "./types";

/**
 * 頁面佈局組件
 * 提供一致的頁面結構，包括導航、側邊欄和內容區域
 */
export function PageLayout(props: PageLayoutProps) {
  const { variant = "app" } = props;

  if (variant === "landing") {
    return <PageLayoutContent {...props} />;
  }

  return (
    <SidebarProvider>
      <PageLayoutContent {...props} />
    </SidebarProvider>
  );
}

/**
 * 頁面內容組件 - 在 SidebarProvider 內部使用 useSidebar
 */
function PageLayoutContent(props: PageLayoutProps) {
  const {
    children,
    title,
    subtitle,
    headerAction,
    maxWidth = "xl",
    // padding = true,
    showNavbar = true,
    showSidebar = true,
    showFooter = true,
    className = "",
    variant = "app",
  } = props;

  const { isOpen, mode } =
    variant === "app" ? useSidebar() : { isOpen: false, mode: "overlay" };

  const getMaxWidthClass = () => {
    switch (maxWidth) {
      case "sm":
        return "max-w-3xl";
      case "md":
        return "max-w-4xl";
      case "lg":
        return "max-w-6xl";
      case "xl":
        return "max-w-7xl";
      case "full":
        return "max-w-full";
      default:
        return "max-w-7xl";
    }
  };

  return (
    <div
      className={`min-h-screen flex flex-col bg-background-default ${className}`}
    >
      {showNavbar && (
        <div className="fixed top-0 left-0 right-0 z-50">
          {variant === "landing" ? <LandingNavbar /> : <Navbar />}
        </div>
      )}

      {showSidebar && variant === "app" && <Sidebar />}

      <div className={`flex flex-1 ${variant === "app" ? "pt-16" : ""}`}>
        <main
          className={`flex-1 flex flex-col transition-all duration-300 min-h-[calc(100vh-4rem)] ${
            variant === "app" && mode === "docked"
              ? isOpen
                ? "ml-64"
                : "ml-16"
              : "ml-0"
          }`}
        >
          <div className="flex-1">
            <div className={`mx-auto`}>
              {(title || subtitle || headerAction) && (
                <div className="py-6 md:flex md:items-center md:justify-between">
                  <div className="min-w-0 flex-1">
                    {title && (
                      <div className="mb-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h1 className="text-2xl font-bold text-text-primary">
                              {title}
                            </h1>
                            {subtitle && (
                              <p className="mt-1 text-text-secondary">
                                {subtitle}
                              </p>
                            )}
                          </div>
                          {headerAction && (
                            <div className="mt-4 sm:mt-0">{headerAction}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div>{children}</div>
            </div>
          </div>

          {showFooter && (
            <footer className="py-4 border-t border-border-light mt-auto">
              <div
                className={`${getMaxWidthClass()} mx-auto px-4 sm:px-6 lg:px-8`}
              >
                <p className="text-center text-sm text-text-secondary">
                  &copy; 2025 Mayi AI. All rights reserved.
                </p>
              </div>
            </footer>
          )}
        </main>
      </div>
    </div>
  );
}
